import { Typography } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { CustomValue } from '@/console/features/jobs/components/jobs-list/jobs-list-renderer.styled';
import { TJobRenderComponentProps } from '@/console/features/jobs/components/jobs-list/jobs-list-renderer.types';
import { ProjectsIcon } from '@/shared/assets/icons/projects-icon';
import { Skeleton } from '@/shared/components/ui/skeleton/skeleton.styled';
import { TruncatedText } from '@/shared/components/ui/truncated-text/truncated-text';
import { useReadProjectsByIdsQuery } from '@/shared/redux/api/assets/projects/projects-api';
import { isPermissionDeniedError } from '@/shared/redux/api/utils/error-handlers';
import { getProtectedResourceLabel } from '@/shared/utils/protected-resource-label';

export const JobProject: React.FC<TJobRenderComponentProps> = ({
  job,
  truncate,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const projectId = job?.projectIds?.[0];
  const {
    data: projectsData,
    error: projectsError,
    isLoading: projectsLoading,
    isFetching: projectsFetching,
  } = useReadProjectsByIdsQuery(
    { projectIds: job?.projectIds! },
    { skip: !job?.projectIds?.length },
  );

  const projectsPending = projectsLoading || projectsFetching;
  const project = projectsData?.readProjectsByIds?.items?.find(
    (project) => project.projectId === projectId,
  );
  const isProjectProtected =
    !project &&
    !projectsPending &&
    Boolean(projectId) &&
    (Boolean(projectsData) || isPermissionDeniedError(projectsError));

  const navigateToProject = () => {
    if (!project) {
      return;
    }
    navigate(`/projects/${project.projectId}`);
  };

  const renderLabel = () => {
    const label =
      project?.name ??
      (isProjectProtected
        ? getProtectedResourceLabel(t, 'project')
        : t('projects:deleted_project'));

    if (truncate) {
      return <TruncatedText variant="p7" maxLength={32} text={label} />;
    }

    return <Typography variant="p7">{label}</Typography>;
  };

  if (!job?.projectIds?.[0]) {
    return null;
  }

  if (projectsPending) {
    return <Skeleton />;
  }

  return (
    <CustomValue onClick={navigateToProject} $clickable={!!project}>
      <ProjectsIcon
        size="1rem"
        variant={project || !isProjectProtected ? 'primary' : 'light'}
      />
      {renderLabel()}
    </CustomValue>
  );
};
